import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft,
  Search,
  Filter,
  Download,
  FileText,
  Calendar,
  Clock,
  AlertTriangle,
  CheckCircle2,
  CircleDot,
  User,
  ChevronRight,
  MoreHorizontal,
  FileSpreadsheet,
  RefreshCw,
  ExternalLink,
  X,
  Crown,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ActionRegisterPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [featureAccess, setFeatureAccess] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("open,in_progress");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  const fetchActionRegister = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append("status", statusFilter);
      if (priorityFilter) params.append("priority", priorityFilter);
      
      const response = await axios.get(`${API}/action-register?${params.toString()}`);
      setItems(response.data.items);
      setSummary(response.data.summary);
    } catch (error) {
      if (error.response?.status === 403) {
        setFeatureAccess({ has_access: false });
      } else {
        toast.error("Failed to load action register");
      }
    }
  }, [statusFilter, priorityFilter]);

  const checkFeatureAccess = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/entitlements/check/action_register`);
      setFeatureAccess(response.data);
      if (response.data.has_access) {
        fetchActionRegister();
      }
    } catch (error) {
      setFeatureAccess({ has_access: false });
    } finally {
      setLoading(false);
    }
  }, [fetchActionRegister]);

  useEffect(() => {
    checkFeatureAccess();
  }, [checkFeatureAccess]);

  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API}/action-register/export/csv`, {
        responseType: "blob"
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `action_register_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("CSV exported successfully");
    } catch (error) {
      toast.error("Failed to export CSV");
    }
  };

  const handleExportPDF = async () => {
    try {
      const response = await axios.get(`${API}/action-register/export/pdf`, {
        responseType: "blob"
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `action_register_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("PDF exported successfully");
    } catch (error) {
      toast.error("Failed to export PDF");
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case "in_progress":
        return <CircleDot className="w-4 h-4 text-blue-500" />;
      default:
        return <Clock className="w-4 h-4 text-slate-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "bg-green-100 text-green-700";
      case "in_progress":
        return "bg-blue-100 text-blue-700";
      case "cancelled":
        return "bg-slate-100 text-slate-500";
      default:
        return "bg-amber-100 text-amber-700";
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high":
        return "bg-red-100 text-red-700 border-red-200";
      case "medium":
        return "bg-amber-100 text-amber-700 border-amber-200";
      default:
        return "bg-slate-100 text-slate-600 border-slate-200";
    }
  };

  const filteredItems = items.filter(item => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      item.title?.toLowerCase().includes(query) ||
      item.assigned_to?.toLowerCase().includes(query) ||
      item.meeting_name?.toLowerCase().includes(query)
    );
  });

  // Feature not available - upgrade prompt
  if (!loading && featureAccess && !featureAccess.has_access) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-slate-50">
        <header className="bg-white/80 backdrop-blur-md border-b border-slate-200/60 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <h1 className="text-xl font-bold text-slate-800">Action Register</h1>
            </div>
          </div>
        </header>

        <div className="max-w-2xl mx-auto px-6 py-20 text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-purple-100 to-purple-200 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Crown className="w-10 h-10 text-purple-500" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-4">
            Premium Feature
          </h2>
          <p className="text-slate-600 mb-8 max-w-md mx-auto">
            The Cross-Meeting Action Register is available on the Premium Governance plan.
            Track all action items across meetings, filter by status, and export reports.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              onClick={() => navigate("/pricing")}
              className="bg-gradient-to-r from-purple-500 to-purple-700 hover:from-purple-600 hover:to-purple-800"
            >
              Upgrade to Premium
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/dashboard")}
            >
              Back to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-slate-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200/60 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-xl font-bold text-slate-800">Action Register</h1>
                <p className="text-sm text-slate-500">Cross-meeting action item tracking</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" onClick={handleExportCSV}>
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
              <Button variant="outline" size="sm" onClick={handleExportPDF}>
                <FileText className="w-4 h-4 mr-2" />
                Export PDF
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-2xl border border-slate-200/60 p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center">
                  <FileText className="w-5 h-5 text-slate-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{summary.total}</p>
                  <p className="text-xs text-slate-500">Total Items</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-red-200/60 p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-red-600">{summary.overdue}</p>
                  <p className="text-xs text-slate-500">Overdue</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-amber-200/60 p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-600">{summary.by_status?.open || 0}</p>
                  <p className="text-xs text-slate-500">Open</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-blue-200/60 p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                  <CircleDot className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-600">{summary.by_status?.in_progress || 0}</p>
                  <p className="text-xs text-slate-500">In Progress</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-green-200/60 p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-600">{summary.by_status?.completed || 0}</p>
                  <p className="text-xs text-slate-500">Completed</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-2xl border border-slate-200/60 p-4 mb-6">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by title, assignee, or meeting..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex items-center gap-3">
              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v === "all" ? "" : v); fetchActionRegister(); }}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="open,in_progress">Active (Open/In Progress)</SelectItem>
                  <SelectItem value="open">Open Only</SelectItem>
                  <SelectItem value="in_progress">In Progress Only</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="all">All Statuses</SelectItem>
                </SelectContent>
              </Select>
              <Select value={priorityFilter} onValueChange={(v) => { setPriorityFilter(v === "all" ? "" : v); fetchActionRegister(); }}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priorities</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="ghost" size="icon" onClick={fetchActionRegister}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Action Items List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-20 bg-white border border-slate-200/60 rounded-2xl">
            <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-800 mb-2">No action items found</h3>
            <p className="text-slate-500">
              {statusFilter ? "Try changing your filters" : "All caught up!"}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredItems.map((item) => (
              <div
                key={item.id}
                className={`bg-white rounded-2xl border p-5 hover:shadow-md transition-all cursor-pointer ${
                  item.is_overdue ? "border-red-200 bg-red-50/30" : "border-slate-200/60"
                }`}
                onClick={() => navigate(`/meetings/${item.meeting_id}`)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      {getStatusIcon(item.status)}
                      <h3 className="font-semibold text-slate-800">{item.title}</h3>
                      {item.is_overdue && (
                        <span className="flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 text-xs font-semibold rounded-full">
                          <AlertTriangle className="w-3 h-3" />
                          {item.days_overdue}d overdue
                        </span>
                      )}
                      {item.carried_forward_count > 0 && (
                        <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-semibold rounded-full">
                          Carried forward ×{item.carried_forward_count}
                        </span>
                      )}
                    </div>
                    
                    {item.description && (
                      <p className="text-sm text-slate-600 mb-3 line-clamp-2">{item.description}</p>
                    )}
                    
                    <div className="flex flex-wrap items-center gap-3 text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
                        {item.status.replace('_', ' ').toUpperCase()}
                      </span>
                      <span className={`px-2 py-1 rounded-full border text-xs font-medium ${getPriorityColor(item.priority)}`}>
                        {item.priority.toUpperCase()}
                      </span>
                      {item.assigned_to && (
                        <span className="flex items-center gap-1 text-slate-600">
                          <User className="w-3 h-3" />
                          {item.assigned_to}
                        </span>
                      )}
                      {item.due_date && (
                        <span className={`flex items-center gap-1 ${item.is_overdue ? "text-red-600" : "text-slate-600"}`}>
                          <Calendar className="w-3 h-3" />
                          Due: {item.due_date}
                        </span>
                      )}
                      <span className="flex items-center gap-1 text-slate-500">
                        <FileText className="w-3 h-3" />
                        {item.meeting_name}
                      </span>
                    </div>
                  </div>
                  
                  <Button variant="ghost" size="icon" className="text-slate-400 hover:text-teal-600">
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
